export interface PSGCLocation {
  code: string;
  name: string;
  oldName?: string;
  subMunicipalityCode?: string | boolean;
  cityCode?: string | boolean;
  municipalityCode?: string | boolean;
  districtCode?: string | boolean;
  provinceCode?: string;
  regionCode?: string;
  regionName?: string;
  islandGroupCode?: string;
  region?: string;
  province?: string;
  city?: string;
}

export interface CacheData {
  data: PSGCLocation[];
  timestamp: number;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const PSGC_API_BASE = 'https://psgc.gitlab.io/api';

/**
 * Get regions from PSGC API with caching
 */
export const getRegions = async (): Promise<PSGCLocation[]> => {
  const cacheKey = 'psgc_regions';
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const { data, timestamp } = JSON.parse(cached) as CacheData;
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  try {
    const response = await fetch(`${PSGC_API_BASE}/regions.json`);
    if (!response.ok) throw new Error('Failed to fetch regions');
    const data = await response.json();
    
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    
    return data;
  } catch (error) {
    console.error('Error fetching regions:', error);
    return [];
  }
};

/**
 * Get provinces from PSGC API with caching
 */
export const getProvinces = async (): Promise<PSGCLocation[]> => {
  const cacheKey = 'psgc_provinces';
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const { data, timestamp } = JSON.parse(cached) as CacheData;
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  try {
    const response = await fetch(`${PSGC_API_BASE}/provinces.json`);
    if (!response.ok) throw new Error('Failed to fetch provinces');
    const data = await response.json();
    
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    
    return data;
  } catch (error) {
    console.error('Error fetching provinces:', error);
    return [];
  }
};

/**
 * Get provinces for a specific region by filtering all provinces
 * 
 * IMPORTANT: This function filters provinces by regionCode.
 * For regions with no provinces (like NCR), this will return an empty array,
 * which will automatically hide the province field in the UI.
 * 
 * Example behavior:
 * - NCR (Metro Manila): returns [] (no provinces, uses districts instead)
 * - Region I: returns [Ilocos Norte, Ilocos Sur, La Union, Pangasinan]
 * - CAR: returns [Abra, Apayao, Benguet, Ifugao, Kalinga, Mountain Province]
 * - All other regions: return their respective provinces
 */
export const getProvincesByRegion = async (regionCode: string): Promise<PSGCLocation[]> => {
  try {
    const allProvinces = await getProvinces();
    const filtered = allProvinces.filter(p => p.regionCode === regionCode);
    
    console.log(`Region ${regionCode}: Found ${filtered.length} provinces`);
    
    return filtered;
  } catch (error) {
    console.error('Error filtering provinces by region:', error);
    return [];
  }
};

/**
 * Get cities/municipalities for a province
 */
export const getCitiesMunicipalities = async (provinceCode: string): Promise<PSGCLocation[]> => {
  const cacheKey = `psgc_cities_${provinceCode}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const { data, timestamp } = JSON.parse(cached) as CacheData;
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  try {
    const response = await fetch(
      `${PSGC_API_BASE}/provinces/${provinceCode}/cities-municipalities.json`
    );
    if (!response.ok) throw new Error('Failed to fetch cities/municipalities');
    const data = await response.json();
    
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    
    return data;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};

/**
 * Get cities/municipalities for a region (when region has no provinces, like NCR)
 */
export const getCitiesByRegion = async (regionCode: string): Promise<PSGCLocation[]> => {
  const cacheKey = `psgc_cities_region_${regionCode}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const { data, timestamp } = JSON.parse(cached) as CacheData;
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  try {
    // Fetch all cities and filter by regionCode
    const response = await fetch(`${PSGC_API_BASE}/cities-municipalities.json`);
    if (!response.ok) throw new Error('Failed to fetch cities/municipalities');
    const allCities = await response.json();
    
    // Filter cities by region code
    const filtered = allCities.filter((c: PSGCLocation) => c.regionCode === regionCode);
    
    localStorage.setItem(cacheKey, JSON.stringify({
      data: filtered,
      timestamp: Date.now()
    }));
    
    return filtered;
  } catch (error) {
    console.error('Error fetching cities by region:', error);
    return [];
  }
};

/**
 * Get barangays for a city/municipality
 */
export const getBarangays = async (cityCode: string): Promise<PSGCLocation[]> => {
  const cacheKey = `psgc_barangays_${cityCode}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const { data, timestamp } = JSON.parse(cached) as CacheData;
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  try {
    const response = await fetch(
      `${PSGC_API_BASE}/cities-municipalities/${cityCode}/barangays.json`
    );
    if (!response.ok) throw new Error('Failed to fetch barangays');
    const data = await response.json();
    
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    
    return data;
  } catch (error) {
    console.error('Error fetching barangays:', error);
    return [];
  }
};

/**
 * Search provinces by name
 */
export const searchProvinces = (
  provinces: PSGCLocation[],
  searchTerm: string
): PSGCLocation[] => {
  if (!searchTerm) return provinces;
  
  const term = searchTerm.toLowerCase();
  return provinces.filter(p => 
    p.name.toLowerCase().includes(term)
  ).slice(0, 15);
};

/**
 * Search cities by name
 */
export const searchCities = (
  cities: PSGCLocation[],
  searchTerm: string
): PSGCLocation[] => {
  if (!searchTerm) return cities;
  
  const term = searchTerm.toLowerCase();
  return cities.filter(c => 
    c.name.toLowerCase().includes(term)
  ).slice(0, 15);
};

/**
 * Search barangays by name
 */
export const searchBarangays = (
  barangays: PSGCLocation[],
  searchTerm: string
): PSGCLocation[] => {
  if (!searchTerm) return barangays;
  
  const term = searchTerm.toLowerCase();
  return barangays.filter(b => 
    b.name.toLowerCase().includes(term)
  ).slice(0, 15);
};

/**
 * Clear location cache
 */
export const clearLocationCache = (): void => {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('psgc_')) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Debug: Log location hierarchy structure
 */
/**
 * Debug: Verify complete Philippine regional structure
 * Run in browser console: locationApi.debugLocationHierarchy()
 * 
 * This will log all 17 regions with their corresponding provinces/districts
 * to verify the system is correctly following the official structure.
 */
export const debugLocationHierarchy = async (): Promise<void> => {
  try {
    const regions = await getRegions();
    const provinces = await getProvinces();
    
    console.log('=== PHILIPPINE REGIONAL STRUCTURE VERIFICATION ===');
    console.log(`Total Regions: ${regions.length}`);
    console.log(`Total Provinces: ${provinces.length}`);
    console.log('');
    
    // Show all regions and their provinces
    const regionMapping: { [key: string]: string[] } = {};
    
    for (const region of regions) {
      const provincesInRegion = provinces.filter(p => p.regionCode === region.code);
      const provinceNames = provincesInRegion.map(p => p.name);
      
      regionMapping[region.name] = provinceNames;
      
      const status = provinceNames.length === 0 ? '(NO PROVINCES - Districts)' : '';
      console.log(`${region.name} (${region.code}) - ${provinceNames.length} ${status}`);
      if (provinceNames.length > 0) {
        console.log(`  → ${provinceNames.join(', ')}`);
      }
    }
    
    console.log('');
    console.log('=== STRUCTURE VERIFICATION COMPLETE ===');
    console.log('Region mapping:', regionMapping);
    
  } catch (error) {
    console.error('Debug error:', error);
  }
};
